import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import StatusDropdown from './StatusDropdown';
import BulkActions from './BulkActions';
import PhotoThumbnails from './PhotoThumbnails';
import KVThumbnails from './KVThumbnails';

const InstallationTable = ({ 
  installations, 
  onStatusUpdate, 
  onStatusTransition,
  onViewDetails, 
  onPhotoUpload,
  onChangeKV
}) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'installationDate', direction: 'desc' });

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedItems(installations?.map(item => item?.id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (itemId, checked) => {
    if (checked) {
      setSelectedItems([...selectedItems, itemId]);
    } else {
      setSelectedItems(selectedItems?.filter(id => id !== itemId));
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedInstallations = [...installations]?.sort((a, b) => {
    if (sortConfig?.key) {
      const aValue = a?.[sortConfig?.key];
      const bValue = b?.[sortConfig?.key];
      
      if (aValue < bValue) {
        return sortConfig?.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig?.direction === 'asc' ? 1 : -1;
      }
    }
    return 0;
  });

  const getSortIcon = (key) => {
    if (sortConfig?.key !== key) {
      return <Icon name="ArrowUpDown" size={14} className="text-muted-foreground" />;
    }
    return sortConfig?.direction === 'asc' 
      ? <Icon name="ArrowUp" size={14} className="text-foreground" />
      : <Icon name="ArrowDown" size={14} className="text-foreground" />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-GB');
  };

  // Enhanced status change handler with audit logging
  const handleStatusChange = (installationId, newStatus, options = {}) => {
    onStatusUpdate?.([installationId], newStatus, {
      user: 'Current User', // In real app, get from auth context
      isUndo: options?.isUndo || false,
      ...options
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {selectedItems?.length > 0 && (
        <BulkActions
          selectedCount={selectedItems?.length}
          onStatusUpdate={(status) => onStatusUpdate(selectedItems, status)}
          onClearSelection={() => setSelectedItems([])}
        />
      )}
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="w-12 p-4">
                <input
                  type="checkbox"
                  checked={selectedItems?.length === installations?.length && installations?.length > 0}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                  className="rounded border-border"
                />
              </th>
              <th className="text-left p-4 font-medium text-foreground">
                <button
                  onClick={() => handleSort('storeName')}
                  className="flex items-center gap-2 hover:text-primary transition-colors"
                >
                  Store Name + Code
                  {getSortIcon('storeName')}
                </button>
              </th>
              <th className="text-left p-4 font-medium text-foreground">Branding Medium</th>
              <th className="text-left p-4 font-medium text-foreground">Branding ID</th>
              <th className="text-left p-4 font-medium text-foreground">KV ID</th>
              <th className="text-left p-4 font-medium text-foreground">
                <button
                  onClick={() => handleSort('status')}
                  className="flex items-center gap-2 hover:text-primary transition-colors"
                >
                  Status
                  {getSortIcon('status')}
                </button>
              </th>
              <th className="text-left p-4 font-medium text-foreground">Photos</th>
              <th className="text-left p-4 font-medium text-foreground">Last Updated</th>
              <th className="w-20 p-4"></th>
            </tr>
          </thead>
          <tbody>
            {sortedInstallations?.map((installation) => (
              <tr key={installation?.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                <td className="p-4">
                  <input
                    type="checkbox"
                    checked={selectedItems?.includes(installation?.id)}
                    onChange={(e) => handleSelectItem(installation?.id, e?.target?.checked)}
                    className="rounded border-border"
                  />
                </td>
                <td className="p-4">
                  <div>
                    <div className="font-medium text-foreground">{installation?.storeName}</div>
                    <div className="text-sm text-muted-foreground">
                      {installation?.storeCode} • {installation?.storeLocation}
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <div className="font-medium text-foreground">{installation?.brandingType}</div>
                  <div className="text-sm text-muted-foreground">{installation?.brandName}</div>
                </td>
                <td className="p-4">
                  <div className="font-mono text-sm font-medium text-primary">
                    {installation?.brandingId}
                  </div>
                </td>
                <td className="p-4">
                  <div className="space-y-2">
                    <KVThumbnails installation={installation} size="sm" />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onChangeKV?.(installation)}
                      className="text-xs h-6 px-2 text-muted-foreground hover:text-primary"
                    >
                      <Icon name="RefreshCw" size={12} />
                      Change KV
                    </Button>
                  </div>
                </td>
                <td className="p-4">
                  <StatusDropdown
                    installation={installation}
                    onStatusChange={handleStatusChange}
                    onPhotoUpload={onPhotoUpload}
                  />
                </td>
                <td className="p-4">
                  <PhotoThumbnails photos={installation?.photos} maxDisplay={3} />
                </td>
                <td className="p-4">
                  <div className="text-foreground">
                    {installation?.createdOn ? formatDate(installation?.createdOn) : formatDate(installation?.installationDate)}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {installation?.assignedTo || installation?.teamLead}
                  </div>
                </td>
                <td className="p-4">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onViewDetails(installation)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <Icon name="Eye" size={16} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="lg:hidden">
        {sortedInstallations?.map((installation) => (
          <div key={installation?.id} className="p-4 border-b border-border last:border-b-0">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={selectedItems?.includes(installation?.id)}
                  onChange={(e) => handleSelectItem(installation?.id, e?.target?.checked)}
                  className="rounded border-border"
                />
                <div>
                  <div className="font-medium text-foreground">{installation?.storeName}</div>
                  <div className="text-sm text-muted-foreground">
                    {installation?.storeCode} • {installation?.storeLocation}
                  </div>
                </div>
              </div>
              <StatusDropdown
                installation={installation}
                onStatusChange={handleStatusChange}
                onPhotoUpload={onPhotoUpload}
                size="sm"
              />
            </div>
            
            <div className="grid grid-cols-1 gap-3 mb-3">
              <div>
                <div className="text-sm text-muted-foreground">Branding Medium + ID</div>
                <div className="font-medium text-foreground">{installation?.brandingType}</div>
                <div className="font-mono text-xs text-primary">{installation?.brandingId}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">KV ID</div>
                <div className="space-y-2">
                  <KVThumbnails installation={installation} size="md" />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onChangeKV?.(installation)}
                    className="text-xs h-6 px-2 text-muted-foreground hover:text-primary"
                  >
                    <Icon name="RefreshCw" size={12} />
                    Change KV
                  </Button>
                </div>
              </div>
              {installation?.photos?.length > 0 && (
                <div>
                  <div className="text-sm text-muted-foreground">Photos</div>
                  <PhotoThumbnails photos={installation?.photos} maxDisplay={4} />
                </div>
              )}
              <div>
                <div className="text-sm text-muted-foreground">Last Updated</div>
                <div className="font-medium text-foreground">
                  {installation?.createdOn ? formatDate(installation?.createdOn) : formatDate(installation?.installationDate)}
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-muted-foreground">
                  Assigned: {installation?.assignedTo || installation?.teamLead}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onViewDetails(installation)}
                >
                  <Icon name="Eye" size={16} />
                  View
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {installations?.length === 0 && (
        <div className="p-12 text-center">
          <Icon name="Package" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No installations found</h3>
          <p className="text-muted-foreground">Try adjusting your filters or create a new installation.</p>
        </div>
      )}
    </div>
  );
};

export default InstallationTable;